import { z } from 'zod';

import { ProjectRole } from '../enums.js';

import { HexColor, Ulid } from './common.js';

/** Field path inside a Lead. Supports `customFields.<key>` for nested access. */
export const FieldPath = z
  .string()
  .regex(
    /^[a-zA-Z][a-zA-Z0-9]*(\.[a-zA-Z0-9_]+)?$/,
    'use camelCase fields, optionally with one customFields.<key> segment',
  );

export const FieldFormat = z.enum(['email', 'e164', 'url', 'iso-date']);

export const ValidationRules = z.object({
  requiredFields: z.array(FieldPath).optional().default([]),
  fieldFormats: z.record(FieldFormat).optional().default({}),
  minValueCents: z.number().int().nonnegative().optional(),
  maxValueCents: z.number().int().nonnegative().optional(),
  documents: z
    .object({
      minCount: z.number().int().nonnegative().optional(),
      maxCount: z.number().int().nonnegative().optional(),
      mimeTypes: z.array(z.string()).optional(),
    })
    .optional(),
});
export type ValidationRules = z.infer<typeof ValidationRules>;

export const Stage = z.object({
  id: Ulid,
  flowchartId: Ulid,
  slug: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'slug must be lowercase kebab-case'),
  name: z.string().min(1).max(80),
  description: z.string().nullable(),
  color: HexColor,
  position: z.number().int().nonnegative(),
  isInitial: z.boolean(),
  isTerminal: z.boolean(),
  slaHours: z.number().int().positive().nullable(),
  validationRules: ValidationRules,
});
export type Stage = z.infer<typeof Stage>;

export const StageCreate = Stage.omit({ id: true, flowchartId: true });
export type StageCreate = z.infer<typeof StageCreate>;

/**
 * Conditions evaluated against the lead before a transition runs.
 * A small expression tree — see blueprint §7.3.
 */
export type Condition =
  | { op: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte'; field: string; value?: unknown }
  | { op: 'in'; field: string; values: unknown[] }
  | { op: 'exists'; field: string }
  | { op: 'all'; conditions: Condition[] }
  | { op: 'any'; conditions: Condition[] };

const baseCondition = z.union([
  z.object({
    op: z.enum(['eq', 'neq', 'gt', 'gte', 'lt', 'lte']),
    field: FieldPath,
    value: z.unknown(),
  }),
  z.object({ op: z.literal('in'), field: FieldPath, values: z.array(z.unknown()) }),
  z.object({ op: z.literal('exists'), field: FieldPath }),
]);

export const Condition: z.ZodType<Condition> = z.lazy(() =>
  z.union([
    baseCondition,
    z.object({ op: z.literal('all'), conditions: z.array(Condition) }),
    z.object({ op: z.literal('any'), conditions: z.array(Condition) }),
  ]),
);

export const SideEffects = z.object({
  notifyAssignee: z.boolean().optional(),
  notifyMentioned: z.boolean().optional(),
  notifyMaster: z.boolean().optional(),
  createReminder: z
    .object({
      offsetMinutes: z.number().int().positive(),
      message: z.string().max(280),
      assigneeUserId: Ulid.optional(),
    })
    .optional(),
  webhook: z
    .object({
      url: z.string().url(),
      headers: z.record(z.string()).optional(),
    })
    .optional(),
  exportSnapshot: z.boolean().optional(),
});
export type SideEffects = z.infer<typeof SideEffects>;

export const Transition = z.object({
  id: Ulid,
  flowchartId: Ulid,
  fromStageId: Ulid,
  toStageId: Ulid,
  name: z.string().min(1).max(80),
  allowedRole: ProjectRole,
  requiresNote: z.boolean(),
  conditions: z.union([z.object({}).strict(), Condition]).optional().default({}),
  sideEffects: SideEffects.optional().default({}),
});
export type Transition = z.infer<typeof Transition>;

export const TransitionCreate = Transition.omit({ id: true, flowchartId: true });
export type TransitionCreate = z.infer<typeof TransitionCreate>;

export const Flowchart = z.object({
  id: Ulid,
  projectId: Ulid,
  version: z.number().int().positive(),
  name: z.string().min(1).max(120),
  description: z.string().nullable(),
  isActive: z.boolean(),
  createdByUserId: Ulid,
  createdAt: z.string().datetime(),
  publishedAt: z.string().datetime().nullable(),
});
export type Flowchart = z.infer<typeof Flowchart>;

export const FlowchartGraph = Flowchart.extend({
  stages: z.array(Stage),
  transitions: z.array(Transition),
});
export type FlowchartGraph = z.infer<typeof FlowchartGraph>;

export const FlowchartCreate = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional(),
});
export type FlowchartCreate = z.infer<typeof FlowchartCreate>;
